import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { OrganizationItemType } from '@mls-wallet/shared-types';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { useReorderOrganizationItems } from './use-reorder-organization-items';

type UseOrganizationItemOrderInput<T> = {
  items: T[];
  itemType: OrganizationItemType;
  getItemId: (item: T) => string;
  /** Disables client-side drag handling; the API independently enforces admin access. */
  enabled: boolean;
};

function arraysEqual(left: string[], right: string[]) {
  return (
    left.length === right.length && left.every((value, i) => value === right[i])
  );
}

function projectOrderToVisibleIds(
  authoritativeIds: readonly string[],
  visibleIds: readonly string[]
): string[] {
  const visibleIdSet = new Set(visibleIds);
  const orderedVisibleIds = authoritativeIds.filter((id) =>
    visibleIdSet.has(id)
  );
  const orderedVisibleIdSet = new Set(orderedVisibleIds);

  // A concurrent create can appear in the fetched collection after the reorder
  // transaction. Keep such rows visible until a later fetch orders them.
  return [
    ...orderedVisibleIds,
    ...visibleIds.filter((id) => !orderedVisibleIdSet.has(id)),
  ];
}

/**
 * Adapts a server-ordered collection to an optimistically reorderable list.
 *
 * The hook preserves its local order through identity-only refetches, applies a
 * drag immediately, and reconciles successful writes from the server response.
 * Polling changes that arrive during a write are deferred so an older request
 * cannot replace the newer optimistic order. A failed write adopts the latest
 * deferred source, or rolls back when the source did not change.
 */
export function useOrganizationItemOrder<T>({
  items,
  itemType,
  getItemId,
  enabled,
}: UseOrganizationItemOrderInput<T>) {
  const sourceIds = useMemo(
    () => items.map((item) => getItemId(item)),
    [getItemId, items]
  );
  // Data hooks may return a new array on every render. Compare the ordered IDs,
  // not array identity, so an unrelated refetch cannot undo an optimistic move.
  const sourceSignature = JSON.stringify(sourceIds);
  const lastSourceSignatureRef = useRef(sourceSignature);
  const latestSourceIdsRef = useRef(sourceIds);
  const pendingOrderRef = useRef<string[] | null>(null);
  const deferredSourceIdsRef = useRef<string[] | null>(null);
  const [orderedIds, setOrderedIds] = useState(sourceIds);
  const [reorderError, setReorderError] = useState<string | null>(null);
  const { reorderOrganizationItems, isReordering } =
    useReorderOrganizationItems();

  latestSourceIdsRef.current = sourceIds;

  useEffect(() => {
    if (lastSourceSignatureRef.current === sourceSignature) {
      return;
    }

    lastSourceSignatureRef.current = sourceSignature;

    if (pendingOrderRef.current) {
      // A poll started before the PATCH can finish after the optimistic move.
      // Defer it until the write outcome establishes which snapshot should win.
      deferredSourceIdsRef.current = sourceIds;
      return;
    }

    // Outside a pending write, the fetched collection remains authoritative for
    // creates, deletes, and reorders made by another administrator.
    setOrderedIds(sourceIds);
    setReorderError(null);
  }, [sourceIds, sourceSignature]);

  const itemsById = useMemo(
    () => new Map(items.map((item) => [getItemId(item), item])),
    [getItemId, items]
  );
  const orderedItems = useMemo(
    () =>
      orderedIds
        .map((id) => itemsById.get(id))
        // Membership can change before the reconciliation effect runs.
        .filter((item): item is T => item !== undefined),
    [itemsById, orderedIds]
  );

  const reorder = useCallback(
    async (activeId: string, overId: string) => {
      if (!enabled || isReordering || pendingOrderRef.current) {
        return;
      }

      const oldIndex = orderedIds.indexOf(activeId);
      const newIndex = orderedIds.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
        return;
      }

      const previousIds = orderedIds;
      const nextIds = arrayMove(previousIds, oldIndex, newIndex);
      pendingOrderRef.current = nextIds;
      deferredSourceIdsRef.current = null;
      // Update the row position before waiting for the persistence round trip.
      setOrderedIds(nextIds);
      setReorderError(null);

      try {
        const response = await reorderOrganizationItems({
          itemType,
          orderedIds: nextIds,
        });

        if (pendingOrderRef.current !== nextIds) {
          return;
        }

        pendingOrderRef.current = null;
        deferredSourceIdsRef.current = null;
        // The response contains the complete collection order, including rows
        // outside a filtered or paginated view. Project it onto visible rows.
        setOrderedIds(
          projectOrderToVisibleIds(
            response.orderedIds,
            latestSourceIdsRef.current
          )
        );
      } catch (error) {
        if (pendingOrderRef.current !== nextIds) {
          return;
        }

        pendingOrderRef.current = null;
        const deferredSourceIds = deferredSourceIdsRef.current;
        deferredSourceIdsRef.current = null;

        if (deferredSourceIds) {
          setOrderedIds(deferredSourceIds);
        } else {
          setOrderedIds((currentIds) =>
            arraysEqual(currentIds, nextIds) ? previousIds : currentIds
          );
        }
        setReorderError(
          getRequestErrorMessage(error, 'Failed to save the new order.')
        );
      }
    },
    [enabled, isReordering, itemType, orderedIds, reorderOrganizationItems]
  );

  return {
    orderedItems,
    orderedIds,
    reorder,
    isReordering,
    reorderError,
  };
}
